// Cascading Make -> Model -> Version -> Year -> Type fitment search.
// Data source: bmw-filters.json (one entry per product variant fitment).

const FIELD_ORDER = ['make', 'model', 'version', 'year', 'type'];

const PLACEHOLDERS = {
  make: 'Select by Make',
  model: 'Select by Model',
  version: 'Select by Version',
  year: 'Select by Year',
  type: 'Select by Type',
};

class BmwFitmentSearch extends HTMLElement {
  connectedCallback() {
    this.data = [];

    this.selects = {};
    FIELD_ORDER.forEach((field) => {
      this.selects[field] = this.querySelector(`[data-field="${field}"]`);
    });

    this.clearButton = this.querySelector('[data-action="clear"]');
    this.searchButton = this.querySelector('[data-action="search"]');
    this.searchUrl = this.dataset.searchUrl || '/search';

    FIELD_ORDER.forEach((field, index) => {
      this.selects[field].addEventListener('change', () => this.handleChange(index));
    });

    this.clearButton.addEventListener('click', () => this.reset());
    this.searchButton.addEventListener('click', () => this.search());

    fetch(this.dataset.filtersUrl)
      .then((response) => response.json())
      .then((data) => {
        this.data = data;
        this.populateField(0, {});
      })
      .catch((error) => console.error('BMW fitment search: failed to load filter data', error));
  }

  // Returns { field: value, ... } for all fields before the given index that have a selection.
  selectionsBefore(index) {
    const selections = {};
    for (let i = 0; i < index; i++) {
      const field = FIELD_ORDER[i];
      const value = this.selects[field].value;
      if (value) selections[field] = value;
    }
    return selections;
  }

  // Builds the unique, ordered option list for the field at `index` given the current selections.
  optionsFor(index, selections) {
    const field = FIELD_ORDER[index];
    const labelKey = `${field}Label`;
    const seen = new Map();

    this.data.forEach((entry) => {
      const matchesPriorSelections = Object.entries(selections).every(
        ([key, value]) => entry[key] === value
      );
      if (matchesPriorSelections && !seen.has(entry[field])) {
        seen.set(entry[field], entry[labelKey]);
      }
    });

    return [...seen.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  populateField(index, selections) {
    const field = FIELD_ORDER[index];
    const select = this.selects[field];
    const options = this.optionsFor(index, selections);

    select.innerHTML = '';

    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = PLACEHOLDERS[field];
    select.appendChild(placeholder);

    options.forEach(({ value, label }) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = label;
      select.appendChild(option);
    });

    select.disabled = options.length === 0;
  }

  resetFieldsFrom(index) {
    for (let i = index; i < FIELD_ORDER.length; i++) {
      const field = FIELD_ORDER[i];
      const select = this.selects[field];
      select.innerHTML = '';

      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = PLACEHOLDERS[field];
      select.appendChild(placeholder);

      select.disabled = true;
    }
  }

  handleChange(index) {
    this.resetFieldsFrom(index + 1);

    const nextIndex = index + 1;
    if (nextIndex < FIELD_ORDER.length) {
      this.populateField(nextIndex, this.selectionsBefore(nextIndex));
    }

    this.updateSearchButton();
  }

  updateSearchButton() {
    const allSelected = FIELD_ORDER.every((field) => this.selects[field].value);
    this.searchButton.disabled = !allSelected;
  }

  reset() {
    FIELD_ORDER.forEach((field) => {
      this.selects[field].value = '';
    });
    this.resetFieldsFrom(1);
    this.populateField(0, {});
    this.updateSearchButton();
  }

  search() {
    const selections = this.selectionsBefore(FIELD_ORDER.length);
    if (Object.keys(selections).length !== FIELD_ORDER.length) return;

    const query = FIELD_ORDER.map((field) => `tag:${field}_${selections[field]}`).join(' ');
    const url = `${this.searchUrl}?q=${encodeURIComponent(query)}&type=product&options%5Bprefix%5D=last`;
    window.location.href = url;
  }
}

customElements.define('bmw-fitment-search', BmwFitmentSearch);
